/**
 * VoiceNoteButton - Microphone button that uses Web Speech API
 * to convert speech to text in Croatian language.
 * Sends each recognized fragment to parent via onTranscript.
 */
import { useState, useRef, useCallback, useEffect } from "react";
import { Mic, MicOff } from "lucide-react";
import { toast } from "sonner";

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

export default function VoiceNoteButton({ onTranscript, disabled = false }) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    setSupported(!!SpeechRecognition);
  }, []);

  const startListening = useCallback(() => {
    if (!SpeechRecognition) {
      toast.error("Vas preglednik ne podrzava glasovni unos. Koristite Chrome ili Edge.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "hr-HR";
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setListening(true);

    recognition.onresult = (event) => {
      // Only process the latest final result
      const last = event.results[event.results.length - 1];
      if (last.isFinal) {
        const text = last[0].transcript.trim();
        if (text) {
          onTranscript(text);
        }
      }
    };

    recognition.onerror = (event) => {
      if (event.error === "not-allowed") {
        toast.error("Pristup mikrofonu nije dozvoljen. Omogucite mikrofon u postavkama preglednika.");
      } else if (event.error !== "no-speech" && event.error !== "aborted") {
        toast.error(`Greska pri snimanju: ${event.error}`);
      }
      setListening(false);
    };

    recognition.onend = () => setListening(false);

    recognitionRef.current = recognition;
    recognition.start();
  }, [onTranscript]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setListening(false);
  }, []);

  const toggleListening = useCallback(() => {
    if (listening) stopListening();
    else startListening();
  }, [listening, startListening, stopListening]);

  useEffect(() => {
    return () => { if (recognitionRef.current) recognitionRef.current.stop(); };
  }, []);

  if (!supported) return null;

  return (
    <button
      type="button"
      onClick={toggleListening}
      disabled={disabled}
      data-testid="voice-note-button"
      title={listening ? "Zaustavi snimanje" : "Glasovna biljeska"}
      className={`p-2 rounded-lg transition-all ${
        listening
          ? "bg-red-500 text-white animate-pulse shadow-lg shadow-red-200"
          : "bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
      } disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {listening ? <MicOff size={18} /> : <Mic size={18} />}
    </button>
  );
}
